import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { InformationCircleIcon, ArrowRightStartOnRectangleIcon } from '@heroicons/react/24/outline';

import type { CountingProblem } from '../types/counting';
import { countingApi } from '../api/countingApi';
import { useCountingSession } from '../hooks/useCountingSession';

import { SessionViewport } from '../components/table-session/SessionViewport';
import { TableFrame } from '../components/table-session/TableFrame';
import { ExplainPanel, hasExpertComment } from '../components/table-session/ExplainPanel';
import { StatusLine } from '../components/table-session/StatusLine';
import { MastheadInfoRow } from '../components/table-session/MastheadInfoRow';
import { SessionDetailsPopup } from '../components/table-session/SessionDetailsPopup';
import { ResultFeedback } from '../components/table-session/ResultFeedback';
import { MASTHEAD_ICON_BUTTON, MASTHEAD_EXIT_TAB } from '../components/table-session/mastheadChrome';

import { PlayTable } from '../components/counting/PlayTable';
import { DIRECTION_FULL_NAMES } from '../components/counting/directionNames';
import { CountingQuestion } from '../components/counting/CountingQuestion';
import { useTrickAnimation } from '../components/counting/useTrickAnimation';

import { AskJeffButton } from '../components/ask-jeff/AskJeffButton';
import { ASK_JEFF_SECTION_COUNTING } from '../types/askJeff';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { useNavigationGuard } from '../hooks/useNavigationGuard';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSessionGuard } from '../contexts/SessionGuardContext';
import { useAuth } from '../hooks/useAuth';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { AuctionPanel } from '../components/table-session/AuctionPanel';

/**
 * Counting practice, rebuilt on the 023 table frame (feature 028).
 *
 * The pre-028 page was preserved at `/counting-practice-old` until the `_old` cleanup; this one holds the
 * canonical route. Nothing a learner is ASKED has changed — same questions, same answers, same
 * grading, same hand selection. What changed is the presentation and where the session ends.
 *
 * **How Counting maps onto the frame.** The play table is not a hand panel: it renders the two
 * visible hands, the played cards, the trick counts, the vulnerability board AND an auction of its
 * own. So the frame runs in its `wide-hand` layout — two columns, the table taking the row's
 * leftover width — and draws no auction panel at all:
 *
 *   hand     → the whole table (`CountingTable`): a rail (board, contract and tricks), the table
 *              itself (the two hands around the played trick on green baize), and one controls
 *              strip beneath them
 *   auction  → passed on DESKTOP ONLY, where it takes a panel of its own above the answer (client,
 *              2026-09-02: that column had the room). Below lg the table draws it beside the rail
 *              and this slot is empty — one auction in the DOM at any width, never two
 *   call     → the question and its verdict
 *   explain  → Jeff's commentary, where the hand carries any
 *   controls → NOT PASSED. The table's controls belong to the trick they scrub through, so the
 *              frame's controls row is empty and its 99px of chrome goes back to the panels
 *
 * It was briefly the other way round — table stripped to the hands, frame's AuctionPanel in the
 * middle — which put the table in a 270px column and clipped it to the played cards alone at every
 * desktop width (measured 2026-08-31). The table is ~600px wide and indivisible; the frame gave.
 *
 * The page still owns the animation and passes it down: the question is withheld until the replay
 * reaches its stop trick, so the call panel has to read the same instance the table displays.
 *
 * **No streak.** Counting does not track one. Every component that shows one takes it as
 * optional and omits the row when it is absent, so nothing here reports "0" for a measurement
 * that was never made.
 */

interface LocationState {
  problems: CountingProblem[];
  totalAvailable: number;
  sessionOrigin: string;
}

export function CountingPracticePage() {
  usePageTitle('Counting Practice — Hawkes Bridge');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = location.state as LocationState | null;

  // --- The by-id diagnostic entry, carried forward from the pre-028 page: /counting-practice?id=N
  //     opens one specific hand, which is how a reported hand gets looked at.
  const idParam = searchParams.get('id');
  const [idProblems, setIdProblems] = useState<CountingProblem[] | null>(null);
  const [idError, setIdError] = useState<string | null>(null);
  const idFetched = useRef(false);

  useEffect(() => {
    if (!idParam || idFetched.current) return;
    idFetched.current = true;
    countingApi
      .getCountingProblemById(Number(idParam))
      .then(res => {
        if (!res.problems?.length) {
          setIdError(`Hand #${idParam} has no counting question.`);
          return;
        }
        setIdProblems(res.problems);
      })
      .catch(() => setIdError(`Hand #${idParam} could not be loaded.`));
  }, [idParam]);

  const problems = useMemo(
    () => idProblems ?? state?.problems ?? [],
    [idProblems, state?.problems],
  );

  const session = useCountingSession(problems);
  const {
    currentProblem, currentIndex, total, score, netScore,
    lastResult, showingResult, isFinished,
  } = session;

  // --- The animation, hoisted so the frame's controls row and the table drive the same one.
  const autoplay = searchParams.get('autoplay') === 'true';
  const allPlayedCards = useMemo(
    () => currentProblem ? [...currentProblem.playedCards, ...currentProblem.currentTrickCards] : [],
    [currentProblem],
  );
  const animation = useTrickAnimation({
    allPlayedCards,
    stopAfterTrick: currentProblem?.stopAfterTrick ?? 1,
    totalTricksPlayed: currentProblem?.totalTricksPlayed ?? 13,
    autoplay,
  });

  // A new hand rewinds the play. `reset` is the hook's own affordance for this.
  useEffect(() => {
    animation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  // Which of its two homes the auction takes — the frame's panel above the answer, or the
  // table's rail row. The width is the frame's `lg:` cut; the height excludes the shortest laptops
  // (1093x614), where a panel above the answer pad put Submit under the fold. The SAME pair of
  // clauses as the desktop block in index.css, so the two can never disagree. A hook, so it sits
  // up here with the others, ahead of the early returns below.
  const isDesktop = useMediaQuery('(min-width: 1024px) and (min-height: 651px)');

  // How the phone row words the declarer. The pre-028 page drew the same label in the same bar
  // and cut the seat to its initial on the SE class; this is that rule verbatim, so a hand that
  // read "Declarer: W" there reads "Declarer: W" here.
  const isShortPhone = useMediaQuery('(max-width: 767px) and (max-height: 599px)');

  // A claimed hand, once the play has reached its last trick. `hasReachedLastTrick` is sticky
  // until the animation is reset, which happens per hand below — so the badge stays for the rest
  // of the hand however the learner rewinds, exactly as the pre-028 page's did.
  const claimedVisible = currentProblem?.claimed === true && animation.hasReachedLastTrick;

  // --- Guards. A counting session is unsaved work: leaving it loses the visit.
  const hasActiveSession = problems.length > 0 && !isFinished;
  const { setHasActiveSession, setLogoutInterceptor, setScoringType } = useSessionGuard();
  const { logout } = useAuth();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useNavigationGuard(hasActiveSession && !isLeaving);

  useEffect(() => {
    const active = hasActiveSession && !isLeaving;
    setHasActiveSession(active);
    setScoringType(currentProblem?.scoring ?? null);
    setLogoutInterceptor(active ? () => setShowLogoutConfirm(true) : null);
    return () => {
      setHasActiveSession(false);
      setScoringType(null);
      setLogoutInterceptor(null);
    };
  }, [hasActiveSession, isLeaving, currentProblem?.scoring,
      setHasActiveSession, setScoringType, setLogoutInterceptor]);

  // --- No session handed over, and no id to fetch: back to where one is started.
  useEffect(() => {
    if (idParam) return;
    if (!state?.problems?.length) navigate('/dashboard', { replace: true });
  }, [state, navigate, idParam]);

  // --- The end of the visit. Feature 028 sends Counting to the shared Session Summary
  //     scorecard; the pre-028 page went to /summary, which now serves only that page.
  const goToSummary = useCallback(() => {
    setIsLeaving(true);
    navigate('/session-summary', {
      replace: true,
      state: {
        kind: 'counting',
        trail: 'Counting',
        sessionOrigin: 'dashboard',
        answered: session.answered,
        correct: score.correct,
        incorrect: score.incorrect,
        skipped: score.skipped,
        score: netScore,
        totalQuestions: session.answered + score.skipped,
        // Streak, best streak and the difficulty breakdown are OMITTED, not zeroed: Counting
        // measures none of them, and the scorecard's rule is to leave out what was never
        // measured rather than fabricate a value for it.
      },
    });
  }, [navigate, session.answered, score, netScore]);

  useEffect(() => {
    if (isFinished) goToSummary();
  }, [isFinished, goToSummary]);

  // Correct / wrong / skipped, for both homes that show them: the desktop status line and the
  // phone ⓘ popup. The pre-028 page showed the same three numbers; the net score alone cannot
  // tell a fresh session from an even one.
  const breakdown = useMemo(
    () => ({ correct: score.correct, incorrect: score.incorrect, skipped: score.skipped }),
    [score.correct, score.incorrect, score.skipped],
  );

  const handleSubmit = useCallback((answer: string) => session.submit(answer), [session]);
  const handleSkip = useCallback(() => session.skip(), [session]);
  const handleNext = useCallback(() => session.next(), [session]);

  if (idError) {
    return (
      <SessionViewport>
        <p className="p-6 text-danger" data-testid="counting-error">{idError}</p>
      </SessionViewport>
    );
  }

  if (!currentProblem) {
    return (
      <SessionViewport>
        <p className="p-6 text-ink-soft" data-testid="counting-loading">Dealing the cards…</p>
      </SessionViewport>
    );
  }

  const busy = animation.animationState === 'PLAYING';

  return (
    <SessionViewport>
      <TableFrame
        layout="wide-hand"
        className="flex-1 bg-felt-bg py-1.5 sm:py-6 padswap:py-0.5"
        subtitle={
          <>
            <span className="hidden md:inline narrow:hidden">Counting</span>
            {/* Phones: the site header already names the page, so repeating it inside the
                frame would spend masthead height on a word already on screen. The h1 keeps
                an sr-only name; what SHOWS is the compact info row. */}
            <span className="sr-only md:hidden narrow:block">Counting</span>
            <MastheadInfoRow
              position={currentIndex + 1}
              poolSize={total}
              handId={currentProblem.tableResultId}
              // No streak passed — Counting keeps none, and the row omits it rather than
              // showing a flame beside a zero.
              score={netScore}
              difficulty={null}
              answered={showingResult}
            />
          </>
        }
        mastheadLinks={
          <button
            type="button"
            onClick={() => setShowLeaveConfirm(true)}
            aria-label="Leave the Table"
            title="Leave the Table"
            className={MASTHEAD_EXIT_TAB}
          >
            {/* The words from md up, the door glyph below it — the same collapse /problems and
                module practice make. Spelled out at every width, "Leave the Table" is ~95px of a
                masthead that already carries the info row, the ⓘ and the speed 🕒, and the tab ran
                off the right edge of a 390px phone (measured 2026-08-31). The accessible name is
                the same sentence either way. */}
            <span className="hidden md:inline narrow:hidden">Leave the Table</span>
            <ArrowRightStartOnRectangleIcon className="h-5 w-5 md:hidden narrow:block" aria-hidden="true" />
          </button>
        }
        mastheadSubtitleAction={
          /* Phones only, as on /problems and module practice (client, 2026-09-01). From md up the
             status line under the frame already carries every fact the popup holds — the id, the
             position, the score, the scoring type and the tournament — so the button opened a
             dialog that repeated the row six inches below it. */
          <button
            type="button"
            onClick={() => setShowDetailsPopup(v => !v)}
            className={`${MASTHEAD_ICON_BUTTON} md:hidden narrow:block`}
            aria-label="Problem details"
            data-testid="masthead-details-button"
          >
            <InformationCircleIcon className="h-5 w-5 text-cream" />
          </button>
        }
        mastheadSubtitleTrailing={
          <>
            {/* Who is declaring — restored to the phone bar (client, 2026-09-03), after the ⓘ,
                which is where the pre-028 page kept it. The strip under the table carries the same
                label from md up and drops it on phones for want of width (five buttons and a slider
                in ~330px), so the two are exact complements and it shows exactly once at any width.
                Spelled out, or cut to the seat's initial on the SE class — the old page's own rule,
                so a hand that read "Declarer: W" there reads "Declarer: W" here.

                A claimed hand no longer takes this slot (client, 2026-09-28): until then the
                "Claimed" badge REPLACED the declarer here, so once the replay reached the claim a
                phone showed the declarer nowhere at all (spec 031, T051). Both now show, the
                declarer cut to its initial while the badge is up — the same letter the SE class
                already reads — so the pair fits the bar the declarer alone used to have. */}
            <span
              className="md:hidden narrow:inline whitespace-nowrap font-sans text-xs font-normal text-sand"
              data-testid="masthead-declarer"
            >
              Declarer:{' '}
              <b className="font-semibold text-cream">
                {isShortPhone || claimedVisible
                  ? currentProblem.declarerDirection
                  : DIRECTION_FULL_NAMES[currentProblem.declarerDirection] ??
                    currentProblem.declarerDirection}
              </b>
            </span>
            {claimedVisible && (
              /* On the table it was a pill in the middle of the baize — fine on a desktop, where
                 that spot is empty, but a phone's trick is ~110px wide, so the badge covered the
                 cards and could not be dismissed (client report, 2026-09-03). Up here it
                 obstructs nothing and, like the old page's, it stays for the rest of the hand. */
              <span
                className="ml-1 md:hidden narrow:inline whitespace-nowrap rounded-full border border-brass
                  bg-cream px-2.5 py-0.5 font-sans text-xs font-bold claimed-blink"
                style={{ color: '#991b1b' }}
                data-testid="masthead-claimed"
              >
                Claimed
              </span>
            )}
          </>
        }
        handLabel={null}
        callLabel="Your answer"
        hand={
          <PlayTable
            problem={currentProblem}
            animation={animation}
            variant="table-session"
            showAuction={!isDesktop}
          />
        }
        auction={
          isDesktop ? (
            <AuctionPanel
              auction={currentProblem.auction}
              vulnerability={currentProblem.vulnerability}
              decisionSeat={currentProblem.declarerDirection}
              // A complete auction — bid and played — so nothing is pending.
              pendingSeat={null}
              rewindOn={[currentProblem.tableResultId]}
            />
          ) : undefined
        }
        call={
          <div className="space-y-3 xnarrow:space-y-2">
            {showingResult && lastResult ? (
              <>
                <ResultFeedback
                  isCorrect={lastResult.isCorrect}
                  answer={lastResult.correctAnswer}
                  userCall={lastResult.userAnswer}
                  // Counting reveals the answer either way: knowing you were right does not
                  // tell you the count you should have arrived at.
                  revealWhenCorrect
                />
                <div className="flex items-stretch gap-2">
                  <button
                    type="button"
                    onClick={handleNext}
                    data-testid="next-question"
                    className="flex-1 rounded-lg bg-primary-600 py-3 text-[15px] font-bold tracking-wide
                      text-white shadow-[0_2px_0_#063d21] hover:bg-primary-700
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600
                      xnarrow:py-2 padswap:py-2"
                  >
                    Next hand
                  </button>
                  {/* Feature 025, Super Editor only — see Skip's slot below. */}
                  <AskJeffButton
                    tableResultId={currentProblem.tableResultId}
                    sectionKey={ASK_JEFF_SECTION_COUNTING}
                    className="shrink-0 whitespace-nowrap"
                  />
                </div>
              </>
            ) : (
              <>
                <CountingQuestion
                  questionType={currentProblem.questionType}
                  questionText={currentProblem.questionText}
                  onSubmit={handleSubmit}
                  disabled={busy}
                  hasReachedStopTrick={animation.hasReachedStopTrick}
                  stopAfterTrick={currentProblem.stopAfterTrick}
                  /* Skip rides in Submit's own row rather than under it (client report,
                     2026-09-03). Stacked, the two full-width buttons cost the table above ~52px,
                     and on a portrait phone the hand panel is the one that gives — which left the
                     auction showing a single bid row at 384x698. `.counting-answer-actions` keeps
                     them stacked at every other width.

                     Ask Jeff (feature 025, Super Editor only; nothing for anyone else) shares Skip's
                     slot, beside it rather than under it: where the actions stack (desktop), a
                     third full-width button came out of the auction's height and hid it. It used to
                     sit in a footer row under the table, where on phones it was alone in its row
                     and that row's height clipped the playback strip (feature 033, T051; moved at
                     the client's choice, 2026-10-15). Where the slot is too narrow for both (the
                     unfolded Fold7, whose answer column is ~110px wide beside the table), Ask Jeff
                     wraps under Skip instead of crushing it to a sliver; that column has height to
                     spare. For everyone else the slot holds Skip alone, exactly as before. */
                  secondaryAction={
                    <div className="flex flex-wrap items-stretch gap-2">
                      <button
                        type="button"
                        onClick={handleSkip}
                        disabled={busy}
                        data-testid="skip-hand"
                        className="min-w-[5.5rem] flex-1 rounded-lg border border-line-strong bg-card py-2 text-[13px]
                          font-semibold text-ink-soft hover:bg-cream
                          focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600
                          disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Skip Hand
                      </button>
                      <AskJeffButton
                        tableResultId={currentProblem.tableResultId}
                        sectionKey={ASK_JEFF_SECTION_COUNTING}
                        className="flex-1 whitespace-nowrap"
                      />
                    </div>
                  }
                />
              </>
            )}
          </div>
        }
        explain={
          showingResult && hasExpertComment(currentProblem.expertComment)
            ? <ExplainPanel expertComment={currentProblem.expertComment} />
            : undefined
        }
        statusLine={
          <div className="hidden md:block narrow:hidden">
            <StatusLine
              position={currentIndex + 1}
              total={total}
              // streak omitted deliberately — see MastheadInfoRow above.
              score={netScore}
              breakdown={breakdown}
              idLabel="ID"
              idValue={currentProblem.tableResultId}
              scoringType={currentProblem.scoring}
              tournamentTitle={currentProblem.tournamentName}
              // The review set stores problems, not hands; Counting serves hands.
              showReviewPin={false}
            />
          </div>
        }
      />

      {showDetailsPopup && (
        <SessionDetailsPopup
          onClose={() => setShowDetailsPopup(false)}
          // streak omitted — the row is left out rather than reporting a run of zero.
          score={netScore}
          breakdown={breakdown}
          handId={currentProblem.tableResultId}
          idLabel="ID"
          scoringType={currentProblem.scoring}
          difficulty={null}
          tournamentTitle={currentProblem.tournamentName}
        />
      )}

      {showLeaveConfirm && (
        <ConfirmDialog
          isOpen
          title="Leave the table?"
          message="Your score for this visit will be shown, and the hands you have not answered stay unseen."
          confirmLabel="Leave"
          cancelLabel="Keep counting"
          onConfirm={() => { setShowLeaveConfirm(false); session.finish(); }}
          onCancel={() => setShowLeaveConfirm(false)}
        />
      )}

      {showLogoutConfirm && (
        <ConfirmDialog
          isOpen
          title="Sign out?"
          message="You are in the middle of a counting session. Signing out ends it."
          confirmLabel="Sign out"
          cancelLabel="Stay"
          onConfirm={() => { setIsLeaving(true); void logout(); }}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </SessionViewport>
  );
}
